import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, InlineBanner, Modal } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import useAccessRights from 'packages/hooks/useAccessRights';
import { ActionStatus, Entity, TemplateAction } from 'packages/hooks/useAccessRights/types';
import roles from 'packages/configs/roles';
import modalContainer from 'packages/common/modalContainer';
import { Template } from 'packages/store/templates/types';
import { Category } from 'packages/store/categories/types';
import { getTemplates } from 'packages/store/templates/templates.selectors';
import { initCategories } from 'packages/store/searchCategory/searchCategory.actions';
import { getCategoriesActionProgress } from 'packages/store/categories/categories.selectors';
import { addTemplatesToCategories } from 'packages/store/categories/categories.actions';
import { handleDeselectAll } from 'packages/store/creatives/creatives.actions';
import { OwnershipFilterType } from '../filters/components/Ownership/types';
import AccessNotificationModal from './accessNotificationModal';
import CategoryMultiSelect from './CategoryMultiSelect';

export const MAX_TEMPLATES_PER_ADD = 100;

const getAssignedCategoryIds = (template: Template) =>
    template.categories?.map(({ id }) => id) ?? (template.categoryId ? [template.categoryId] : []);

const useTemplateAddToCategoryModal = ({ isOverview = false }: { isOverview?: boolean }) => {
    const [addModalTemplates, setMoveModalTemplate] = useState<Template[] | null>(null);
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
    const { checked: selectedTemplates } = useSelector(getTemplates);
    const { movingInProgress } = useSelector(getCategoriesActionProgress);
    const { isEntityActionAllowed, isAccountRoleAssigned } = useAccessRights();
    const isAdmin = isAccountRoleAssigned(roles.video_brand_studio.handler.admin);
    const { translate } = useTranslate();
    const dispatch = useThunkDispatch();

    const [accessNotificationModal, setAccessNotificationModal] = useState<
        { allowedTemplateIds: number[] | undefined } | undefined
    >(undefined);

    const showModal = addModalTemplates !== null;
    const isOverLimit = (addModalTemplates?.length ?? 0) > MAX_TEMPLATES_PER_ADD;

    const allowedTemplateIds = addModalTemplates?.reduce((acc: number[], template: Template) => {
        const status = isEntityActionAllowed(TemplateAction.MOVE, Entity.TEMPLATE, template.grantedPermissions);

        if (status !== ActionStatus.NOT_ALLOWED && status !== ActionStatus.DISABLED) {
            acc.push(template.id);
        }

        return acc;
    }, []);

    const disabledIds = useMemo(
        () => (addModalTemplates?.length === 1 ? getAssignedCategoryIds(addModalTemplates[0]) : []),
        [addModalTemplates],
    );

    const onModalClose = () => {
        setMoveModalTemplate(null);
        setSelectedCategories([]);
    };

    const onAddTemplates = () => {
        if (!allowedTemplateIds?.length) {
            setAccessNotificationModal({ allowedTemplateIds });
        } else {
            if (selectedTemplates.size && allowedTemplateIds.length !== selectedTemplates.size) {
                setAccessNotificationModal({ allowedTemplateIds });
            }

            dispatch(
                addTemplatesToCategories({
                    templateIds: allowedTemplateIds,
                    categoryIds: selectedCategories.map(({ id }) => id),
                    isOverview,
                    isEditor: false,
                }),
            );
        }

        dispatch(handleDeselectAll());
        onModalClose();
    };

    useLayoutEffect(() => {
        if (showModal) {
            dispatch(
                initCategories({
                    sort: ['NAME', 'ASC'],
                    type: 'CREATIVE',
                    subType: 'TEMPLATE',
                    size: 1000,
                    ownershipSource: isAdmin ? OwnershipFilterType.ALL : OwnershipFilterType.OWNED_BY_ANYONE,
                    includeEmptyCollection: true,
                }),
            );
        }
    }, [showModal, dispatch, isAdmin]);

    const renderModalTemplateMove = () =>
        !accessNotificationModal ? (
            <Modal
                container={modalContainer}
                title={translate('modal.template.add.title')}
                isOpen={showModal}
                onClose={onModalClose}
                actionPrimary={
                    <Button
                        variant="primary"
                        onClick={onAddTemplates}
                        isDisabled={!selectedCategories.length || isOverLimit}
                        isLoading={movingInProgress}
                        title={translate('modal.template.add.add')}
                    >
                        {translate('modal.template.add.add')}
                    </Button>
                }
                actionSecondary={
                    <Button variant="secondary" onClick={onModalClose} title={translate('modal.cancel.title')}>
                        {translate('modal.cancel.title')}
                    </Button>
                }
            >
                {isOverLimit && <InlineBanner variant="warning">{translate('modal.template.add.limit')}</InlineBanner>}
                <CategoryMultiSelect
                    selected={selectedCategories}
                    onChange={setSelectedCategories}
                    disabledIds={disabledIds}
                />
            </Modal>
        ) : (
            <AccessNotificationModal
                title={translate('modal.template.add.title')}
                description={
                    !accessNotificationModal.allowedTemplateIds?.length
                        ? translate('modal.template.access.not-allowed')
                        : translate('modal.template.access.not-all', { action: 'add' })
                }
                loading={movingInProgress}
                isOpen={!!accessNotificationModal}
                toggleOpen={() => setAccessNotificationModal(undefined)}
            />
        );

    return {
        setMoveModalTemplate,
        renderModalTemplateMove,
    };
};

export default useTemplateAddToCategoryModal;
